import React, { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Edit2, Trash2, CheckCircle2, X, ExternalLink, FileDown } from "lucide-react";
import TechnicalSheetForm from "./TechnicalSheetForm";
import { jsPDF } from "jspdf";
import moment from "moment";
import { toast } from "sonner";

export default function TechnicalSheetDetail({
  open,
  onClose,
  sheet,
  onUpdate,
  onDelete,
}) {
  const [editMode, setEditMode] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    {
      setDeleting(true);
      try {
        await onDelete(sheet.id);
        setDeleting(false);
        onClose();
      } catch (error) {
        toast.error("Error: " + error.message);
        setDeleting(false);
      }
    }
  };

  const statusBadges = {
    draft: { className: "bg-gray-100 text-gray-700", label: "Esborrany" },
    pending_review: { className: "bg-amber-100 text-amber-700", label: "Pendent revisió" },
    validated: { className: "bg-emerald-100 text-emerald-700", label: "Validada" },
    rejected: { className: "bg-red-100 text-red-700", label: "Rebutjada" },
  };

  const sourceBadges = {
    manual: { className: "bg-blue-100 text-blue-700", label: "Manual" },
    photo: { className: "bg-purple-100 text-purple-700", label: "Foto" },
    pdf: { className: "bg-indigo-100 text-indigo-700", label: "PDF" },
    internet_ai: { className: "bg-pink-100 text-pink-700", label: "IA + Internet" },
    supplier_document: { className: "bg-cyan-100 text-cyan-700", label: "Proveïdor" },
  };

  const handleExportPDF = () => {
    const doc = new jsPDF();
    const s = sheet;

    // Header bar
    doc.setFillColor(123, 45, 63);
    doc.rect(0, 0, 210, 28, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16);
    doc.setFont(undefined, "bold");
    doc.text("FITXA TÈCNICA DE PRODUCTE", 14, 12);
    doc.setFontSize(10);
    doc.setFont(undefined, "normal");
    doc.text(`Generat per Ho-Sys · ${moment().format("DD/MM/YYYY HH:mm")}`, 14, 22);

    let y = 40;

    const field = (label, value) => {
      if (!value) return;
      if (y > 270) { doc.addPage(); y = 20; }
      doc.setFontSize(8);
      doc.setTextColor(120, 120, 120);
      doc.setFont(undefined, "bold");
      doc.text(label.toUpperCase(), 14, y);
      doc.setFontSize(10);
      doc.setTextColor(30, 30, 30);
      doc.setFont(undefined, "normal");
      const lines = doc.splitTextToSize(String(value), 182);
      doc.text(lines, 14, y + 5);
      y += 5 + lines.length * 6 + 4;
    };

    const sectionTitle = (title) => {
      if (y > 265) { doc.addPage(); y = 20; }
      y += 2;
      doc.setFillColor(245, 245, 248);
      doc.rect(14, y - 4, 182, 8, "F");
      doc.setFontSize(9);
      doc.setFont(undefined, "bold");
      doc.setTextColor(123, 45, 63);
      doc.text(title.toUpperCase(), 16, y + 1);
      y += 10;
    };

    // Nom comercial
    doc.setFontSize(15);
    doc.setFont(undefined, "bold");
    doc.setTextColor(30, 30, 30);
    doc.text(s.commercial_name || "—", 14, y);
    y += 10;

    // Status & source
    const statusLabel = { draft: "Esborrany", pending_review: "Pendent revisió", validated: "Validada", rejected: "Rebutjada" };
    const sourceLabel = { manual: "Manual", photo: "Foto", pdf: "PDF", internet_ai: "IA + Internet", supplier_document: "Proveïdor" };
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.setFont(undefined, "normal");
    doc.text(`Estat: ${statusLabel[s.review_status] || "—"}   ·   Font: ${sourceLabel[s.source_type] || "—"}${s.ai_confidence ? `   ·   Confiança IA: ${s.ai_confidence}%` : ""}`, 14, y);
    y += 10;

    sectionTitle("Informació bàsica");
    field("Marca", s.brand);
    field("Fabricant", s.manufacturer);
    field("Codi producte", s.product_code);
    field("País d'origen", s.origin_country);
    field("Pes net", s.net_weight);
    field("Format", s.format_description);

    if (s.description) { sectionTitle("Descripció"); field("", s.description); }
    if (s.ingredients_text) { sectionTitle("Ingredients / Composició"); field("", s.ingredients_text); }
    if (s.allergens) { sectionTitle("Al·lèrgens declarats"); field("", s.allergens); }

    sectionTitle("Conservació i vida útil");
    field("Condicions de conservació", s.storage_conditions);
    field("Vida útil", s.shelf_life);

    if (s.observations) { sectionTitle("Observacions"); field("", s.observations); }
    if (s.reviewed_by) { field("Revisat per", s.reviewed_by); }

    doc.save(`fitxa_tecnica_${(s.commercial_name || "producte").replace(/\s+/g, "_")}.pdf`);
  };

  const reviewStatus = statusBadges[sheet?.review_status] || statusBadges.draft;
  const sourceStatus = sourceBadges[sheet?.source_type] || sourceBadges.manual;

  if (editMode && sheet) {
    return (
      <TechnicalSheetForm
        open={editMode}
        onClose={() => setEditMode(false)}
        onSave={(data) => {
          onUpdate(sheet.id, data);
          setEditMode(false);
          onClose();
        }}
        initialData={sheet}
      />
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{sheet?.commercial_name || "Fitxa tècnica"}</DialogTitle>
        </DialogHeader>

        {sheet && (
          <div className="space-y-6">
            {/* Header info */}
            <div className="grid grid-cols-2 gap-3 pb-4 border-b">
              <div>
                <p className="text-xs text-gray-500 uppercase font-semibold">Marca</p>
                <p className="text-sm font-medium">{sheet.brand || "—"}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase font-semibold">Fabricant</p>
                <p className="text-sm font-medium">{sheet.manufacturer || "—"}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase font-semibold">Codi producte</p>
                <p className="text-sm font-medium">{sheet.product_code || "—"}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase font-semibold">País origen</p>
                <p className="text-sm font-medium">{sheet.origin_country || "—"}</p>
              </div>
            </div>

            {/* Status badges */}
            <div className="flex flex-wrap gap-2">
              <Badge className={`${reviewStatus.className} border-0`}>
                {reviewStatus.label}
              </Badge>
              <Badge className={`${sourceStatus.className} border-0`}>
                {sourceStatus.label}
              </Badge>
              {sheet.ai_confidence && (
                <Badge className="bg-gray-100 text-gray-700 border-0">
                  Confiança IA: {sheet.ai_confidence}%
                </Badge>
              )}
            </div>

            {/* Description */}
            {sheet.description && (
              <div>
                <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Descripció</p>
                <p className="text-sm text-gray-700">{sheet.description}</p>
              </div>
            )}

            {/* Ingredients */}
            {sheet.ingredients_text && (
              <div>
                <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Ingredients</p>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{sheet.ingredients_text}</p>
              </div>
            )}

            {/* Allergens */}
            {sheet.allergens && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Al·lèrgens</p>
                <p className="text-sm font-medium text-amber-900">{sheet.allergens}</p>
              </div>
            )}

            {/* Storage & shelf life */}
            <div className="grid grid-cols-2 gap-4">
              {sheet.storage_conditions && (
                <div>
                  <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Conservació</p>
                  <p className="text-sm font-medium">{sheet.storage_conditions}</p>
                </div>
              )}
              {sheet.shelf_life && (
                <div>
                  <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Vida útil</p>
                  <p className="text-sm font-medium">{sheet.shelf_life}</p>
                </div>
              )}
            </div>

            {/* Format & weight */}
            <div className="grid grid-cols-2 gap-4">
              {sheet.net_weight && (
                <div>
                  <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Pes net</p>
                  <p className="text-sm font-medium">{sheet.net_weight}</p>
                </div>
              )}
              {sheet.format_description && (
                <div>
                  <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Format</p>
                  <p className="text-sm font-medium">{sheet.format_description}</p>
                </div>
              )}
            </div>

            {/* Source info */}
            <div className="bg-gray-50 rounded-lg p-3 space-y-2">
              {sheet.source_link && (
                <div className="flex items-start gap-2">
                  <p className="text-xs text-gray-500 uppercase font-semibold">Font</p>
                  <a
                    href={sheet.source_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                  >
                    Veure font <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              )}
              {sheet.source_file_url && (
                <div className="flex items-start gap-2">
                  <p className="text-xs text-gray-500 uppercase font-semibold">Fitxer original</p>
                  <a
                    href={sheet.source_file_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:text-blue-700"
                  >
                    Descarregar
                  </a>
                </div>
              )}
              {sheet.reviewed_by && (
                <div>
                  <p className="text-xs text-gray-500 uppercase font-semibold">Revisat per</p>
                  <p className="text-xs text-gray-700">{sheet.reviewed_by}</p>
                </div>
              )}
            </div>

            {/* Observations */}
            {sheet.observations && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-xs text-gray-500 uppercase font-semibold mb-1">Observacions</p>
                <p className="text-sm text-gray-700">{sheet.observations}</p>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Tancar</Button>
          <Button variant="outline" onClick={handleExportPDF} className="text-gray-600 hover:text-gray-800">
            <FileDown className="w-4 h-4 mr-2" /> PDF
          </Button>
          <Button
            variant="outline"
            onClick={() => setEditMode(true)}
            className="text-blue-600 hover:text-blue-700"
          >
            <Edit2 className="w-4 h-4 mr-2" /> Editar
          </Button>
          <Button
            variant="outline"
            onClick={handleDelete}
            disabled={deleting}
            className="text-red-600 hover:text-red-700"
          >
            <Trash2 className="w-4 h-4 mr-2" /> Eliminar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}